import type { AgencyId } from "@inrev/common";
import { useAdminSubmitBondRequest } from "@portal/api/bond-request/admin";
import { useFetchAccountPreviews } from "@portal/api/previews";
import { Modal } from "@portal/components/layout/Modal";
import { SectionNavLayout } from "@portal/components/multiform/SectionNavLayout";
import type { StepConfig } from "@portal/components/multiform/types";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { Card } from "@portal/components/ui/Card";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import type { BondRequestFlowAnalytics } from "@portal/hooks/useBondRequestFlowAnalytics";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, type UseFormReturn, useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import type { BondFormData } from "../../../../types";
import {
	BondRequestCreateContext,
	type PrincipalCompanyOption,
} from "../../../agent/request/create/BondRequestCreateView";
import { transformBondFormForSubmission } from "../../../agent/request/create/schema";
import { FullSubmitModalHeader } from "../../shared/submission/FullSubmitModalHeader";
import {
	filterPrincipalPreviewsByAgency,
	getPrincipalPreviewsMapFromAccountPreviewsWithAgency,
} from "../../shared/submission/getPrincipalPreviewsMapWithAgency";
import {
	AdminBondRequestContext,
	type AdminBondRequestSelectedAccount,
} from "./AdminBondRequestContext";
import {
	type AdminBondRequestFormData,
	adminBondRequestFormSchema,
	getDefaultAdminBondRequestFormData,
} from "./schema";
import { bondStepConfig } from "./steps/bond/Bond.config";
import { bondAdditionalInfoStepConfig } from "./steps/bond-additional-info/BondAdditionalInfo.config";
import { bondTermsStepConfig } from "./steps/bond-terms/BondTerms.config";
import { principalStepConfig } from "./steps/principal/Principal.config";

const defaultBondFormData: BondFormData = {
	templateId: "",
	upload: { type: "", files: [] },
};

const steps: StepConfig<AdminBondRequestFormData>[] = [
	principalStepConfig,
	bondStepConfig,
	bondAdditionalInfoStepConfig,
	bondTermsStepConfig,
];

type AdminBondRequestCreateModalProps = {
	onClose: () => void;
	defaultAgencyId?: AgencyId;
	externalFormMethods?: UseFormReturn<AdminBondRequestFormData>;
	/**
	 * Owned by the surface the admin entered from, so escalating a quick submit
	 * to this modal stays one flow rather than reporting a completion with no
	 * matching Entered.
	 */
	analytics: BondRequestFlowAnalytics;
};

export const AdminBondRequestCreateModal = ({
	onClose,
	defaultAgencyId,
	externalFormMethods,
	analytics,
}: AdminBondRequestCreateModalProps) => {
	const [, setSearchParams] = useSearchParams();
	const setSearchParamsRef = useRef(setSearchParams);
	setSearchParamsRef.current = setSearchParams;

	useEffect(() => {
		return () => {
			setSearchParamsRef.current(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete("section");
					next.delete("st");
					return next;
				},
				{ replace: true },
			);
		};
	}, []);

	return (
		<AdminBondRequestCreateForm
			onClose={onClose}
			defaultAgencyId={defaultAgencyId}
			externalFormMethods={externalFormMethods}
			analytics={analytics}
		/>
	);
};

const AdminBondRequestCreateForm = ({
	onClose,
	defaultAgencyId,
	externalFormMethods,
	analytics,
}: AdminBondRequestCreateModalProps) => {
	const { submitBondRequest, isSubmitting } = useAdminSubmitBondRequest();
	const { accountPreviews, accountPreviewsIsLoading } = useFetchAccountPreviews();

	const principalPreviewsMap = useMemo(
		() => getPrincipalPreviewsMapFromAccountPreviewsWithAgency(accountPreviews || []),
		[accountPreviews],
	);

	const [bondFormData, setBondFormData] = useState<BondFormData>(defaultBondFormData);
	const bondFormRef = useRef(bondFormData);
	bondFormRef.current = bondFormData;

	const [selectedAccount, setSelectedAccount] = useState<AdminBondRequestSelectedAccount | null>(
		null,
	);

	const { onEntered } = analytics;
	useEffect(() => {
		if (selectedAccount) onEntered(selectedAccount.id);
	}, [selectedAccount, onEntered]);

	const defaultValues = useMemo(() => {
		const defaults = getDefaultAdminBondRequestFormData();
		if (defaultAgencyId) {
			defaults.agencyId = defaultAgencyId;
		}
		return defaults;
	}, [defaultAgencyId]);

	const internalFormMethods = useForm<AdminBondRequestFormData>({
		mode: "onBlur",
		resolver: createFormResolver(adminBondRequestFormSchema),
		defaultValues,
	});
	const formMethods = externalFormMethods ?? internalFormMethods;

	const selectedAgencyId = useWatch({
		control: formMethods.control,
		name: "agencyId",
	});

	const filteredPrincipalPreviewsMap = useMemo(
		() => filterPrincipalPreviewsByAgency(principalPreviewsMap, selectedAgencyId),
		[selectedAgencyId, principalPreviewsMap],
	);

	const companies = useMemo((): PrincipalCompanyOption[] => {
		if (!selectedAccount) return [];
		return selectedAccount.companies.map((c) => ({
			id: c.id,
			name: c.name,
			isPrimary: c.id === selectedAccount.primaryCompanyId,
		}));
	}, [selectedAccount]);

	const handleSuccess = async (data: AdminBondRequestFormData) => {
		submitBondRequest({
			data,
			accountId: data.suretyAccountId,
			onSuccess: (bondRequest) => {
				analytics.onCreated(data.suretyAccountId, bondRequest.id);
				onClose();
			},
			onError: (error) =>
				analytics.onFailed(
					data.suretyAccountId,
					error instanceof Error ? error.name : "Unknown error",
				),
		});
	};

	useEffect(() => {
		const bondForm = transformBondFormForSubmission(bondFormData);
		formMethods.setValue("quote.bondForm", bondForm ?? null);
	}, [bondFormData, formMethods]);

	if (accountPreviewsIsLoading) {
		return (
			<Modal onClickOutside={onClose} itemClassName="py-[10px] px-[9px] w-[100vw] h-[100vh]">
				<Card className="flex flex-col w-full h-full max-h-full items-center justify-center">
					<CenteredSpinner />
				</Card>
			</Modal>
		);
	}

	return (
		<AdminBondRequestContext.Provider
			value={{
				principalPreviewsMap,
				filteredPrincipalPreviewsMap,
				selectedAccount,
				setSelectedAccount,
			}}
		>
			<BondRequestCreateContext.Provider value={{ bondFormData, setBondFormData, companies }}>
				<Modal onClickOutside={onClose} itemClassName="py-[10px] px-[9px] w-[100vw] h-[100vh]">
					<Card className="flex flex-col w-full h-full max-h-full">
						<div className="w-full px-5 py-4 border-b border-bc-default flex-shrink-0 rounded-t-md">
							<FullSubmitModalHeader
								title="New Bond Request Submission"
								onClose={onClose}
								closeTrackingId="bond-request-admin-full-close"
							/>
						</div>
						<div className="w-full flex-1 overflow-y-auto rounded-b-md">
							<FormProvider {...formMethods}>
								<FormSchemaProvider value={adminBondRequestFormSchema}>
									<SectionNavLayout
										formMethods={formMethods}
										steps={steps}
										confirmationMessage="Are you sure you want to submit?"
										isSubmitting={isSubmitting}
										scrollable={false}
										onSuccess={handleSuccess}
										trackingPrefix="bond-request-admin-full"
									/>
								</FormSchemaProvider>
							</FormProvider>
						</div>
					</Card>
				</Modal>
			</BondRequestCreateContext.Provider>
		</AdminBondRequestContext.Provider>
	);
};
